/* eslint-disable react/jsx-props-no-spreading */
import { motion } from "framer-motion";
import {
  ReactElement,
  useState,
  KeyboardEvent,
  useRef,
  useEffect,
} from "react";

interface Props {
  children: ReactElement;
  expanded?: boolean;
  className?: string;
  expandClass?: string;
  allowCollapse?: boolean;
}

export default function ExpandableElement({
  expanded: isExpanded = false,
  expandClass = "",
  className = "",
  allowCollapse = true,
  children,
}: Props) {
  const [expanded, setExpanded] = useState(isExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > 88);
    }
  }, [children]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((allowCollapse && event.key === "Enter") || event.key === " ") {
      setExpanded(!expanded);
    }
  };

  const dynamicProps = allowCollapse
    ? {
        onClick: () => (allowCollapse ? setExpanded(!expanded) : false),
        onKeyDown: handleKeyDown,
        role: "button",
        tabIndex: 0,
        "aria-expanded": expanded,
      }
    : {};

  return (
    <div className="relative group" {...dynamicProps}>
      <motion.div
        className={`text-lg leading-relaxed overflow-hidden ${
          !expanded && isOverflowing ? `${expandClass} fade-bottom-sm` : ""
        } ${className}`}
        animate={{
          height: expanded
            ? contentRef.current?.scrollHeight || "auto"
            : "5.5rem",
        }}
        initial={false}
        transition={{
          type: "spring",
          stiffness: 150,
          damping: 15,
          mass: 0.8,
        }}
      >
        <div ref={contentRef} className="review wrap-anywhere max-w-full">
          {children}
        </div>
        {!expanded && isOverflowing && (
          <div className="absolute bottom-0 left-0 right-0 h-14" />
        )}
      </motion.div>
    </div>
  );
}
